import { createRouter, createWebHistory } from 'vue-router'
import userService from '../services/user.service'
import systemService from '../services/system.service'
import project from '/public/static/project.json'

const routes = [
    {
        path: '/', redirect: '/main'
    }, {
        // 用户登录
        name: 'login',
        path: '/login',
        component: () => import('../components/common/login')
    }, {
        // 带有凭证的登录（微信登录code、切换账套token）
        name: 'login-passport',
        path: '/login-passport',
        component: () => import('../components/common/login-passport')
    }, {
        // 重置密码
        name: 'reset-password',
        path: '/reset-password',
        component: () => import('../components/common/reset-password')
    }, {
        path: '/:catchAll(.*)',
        redirect: '/'
    }
]

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes
})

// 检查 project 是否有定义主界面
if (project.main_form) {
    const mainForm = project.main_form
    // 加载用户定义的主界面
    router.addRoute({
        name: 'main',
        path: '/main',
        component: () => import(`@/components/${mainForm}`),
    })
} else {
    // 加载默认主界面
    router.addRoute({
        name: 'main',
        path: '/main',
        component: () => import('../components/tablet/main'),
        children: [
            // 默认主界面显示看板列表
            {
                name: 'list',
                path: "",
                components: {
                    default: () => import('../components/tablet/list'),
                    toolbar: () => import('../components/tablet/list-toolbar')
                },
                props: { default: true }
            }
        ]
    })
}

// 个人信息
router.addRoute('main', {
    name: "profile",
    path: "profile",
    meta: { title: '个人信息' },
    components: {
        default: () => import('../components/tablet/profile'),
        toolbar: () => import('../components/tablet/panel-toolbar')
    }
})

// 根据用户类型加载权限路由
// 超级管理员有账套管理功能、用户管理功能
// 管理员有用户管理功能
const userType = userService?.user?.user_type ?? 'user'
if (userType === 'root') {
    router.addRoute('main', {
        name: "options",
        path: "options",
        meta: { title: '系统参数' },
        components: {
            default: () => import('../components/tablet/options'),
            toolbar: () => import('../components/tablet/panel-toolbar')
        },
        props: { default: true, toolbar: { showToolbar: true } }
    })
}
if (userType === 'root') {
    router.addRoute('main', {
        name: "sobs",
        path: "sobs",
        meta: { title: '账套管理' },
        components: {
            default: () => import('../components/tablet/sobs'),
            toolbar: () => import('../components/tablet/panel-toolbar')
        },
        props: { default: true, toolbar: { showToolbar: true } }
    })
}
if (userType === 'root' || userType === 'admin') {
    router.addRoute('main', {
        name: "user",
        path: "user",
        meta: { title: '用户权限' },
        components: {
            default: () => import('../components/tablet/user'),
            toolbar: () => import('../components/tablet/panel-toolbar')
        },
        props: { default: true }
    })
}

// 加载权限路由（未登录时是空数组，登录后会刷新页面重新加载）
userService.routes.forEach(el => {
    router.addRoute('main', {
        path: el.name,
        name: el.name,
        meta: { title: el.caption },
        components: {
            default: () => import(`@/components/${el.name}`),
            toolbar: () => import('../components/tablet/panel-toolbar')
        },
        // url 参数转为 props，用于页面传参
        props: { default: true }
    })
})

// 不需要校验权限的路由
const publicRoute = ['login', 'login-passport', 'reset-password']
let tag = false

router.beforeEach((to, from, next) => {
    // 不启用登录时所有路由都可以跳转
    if (project.enabled_login === false) {
        next()
        return
    }

    // 如果已经登录，则所有路由都可以跳转
    if (userService.isLogin) {
        if (!tag && to.name === 'list') {
            tag = true

            userService.getPropertyValue('last_open_route_name').then(name => {
                // 检查路由是否存在（可能页面被删除或无权限打开）
                const isExist = router.hasRoute(name)
                if (isExist) {
                    next({ name: name })
                } else {
                    next()
                }
            })
            return
        } else {
            // 非list下刷新或使用url条件需要更改标记，否则首次到 list 会因标记问题又回到最后一次页面
            tag = true
        }

        next()
        return
    }

    // 未登录时只有免校验权限的路由可以跳转，其它路由都跳转到登录界面
    if (publicRoute.indexOf(to.name) === -1) {
        if (systemService.isWeixinBrowser) {
            // 微信浏览器时跳转到微信授权后返回 code 到登录界面，登录界面再拿 code 调用后台接口进行登录
            // 需要注意的是扫码登录也是获得 code 但微信浏览器使用的是公众号appid，而扫码登录是网站应用appid
            window.location.href = 
                'https://open.weixin.qq.com/connect/oauth2/authorize?' +
                `appid=${process.env.VUE_APP_MPAPPID}` + 
                `&redirect_uri=${encodeURIComponent(window.location.origin + process.env.BASE_URL + 'login-passport')}` +
                '&response_type=code&scope=snsapi_userinfo&state=STATE%23wechat_redirect&connect_redirect=1#wechat_redirect'
            return
        } else {
            next({ name: 'login', params: { referrer: to.fullPath } })
        }
        return
    }
    next()
})

export default router